import { PhoneIcon } from "@heroicons/react/24/outline";
import { ActivityParentEntityId, toPhoneNumberE164 } from "@sp-crm/core";
import { useAgendaRefresh } from "components/marketing-plan/use-agenda-refresh";
import {
    activityRecordTypeForEntityKind,
    useDefaultActivityTypeId,
} from "components/tasks/activity-type-options";
import { telecomAvailable } from "components/telecom/phone-action";
import { PhoneCall } from "components/telecom/phone-call";
import { SendSmsPanel } from "components/telecom/send-sms-panel";
import { Panel } from "components/ui/panel/panel";
import { PanelType } from "components/ui/panel/panel-type";
import { AdvancedSearchEntityType } from "generated/graphql";
import React from "react";
import { usePhoneLines } from "store/selectors/telecom";
import { parsePhone } from "util/phone";
import {
    ContactValueLabel,
    ContactValueRow,
    contactValueTextClasses,
} from "./display-row";
import { PhoneChooserPanel } from "./phone-chooser-panel";
import {
    DevicePhoneActions,
    PhoneActionIntent,
    PhoneTriggerBinding,
    PhoneTriggerLink,
    phoneHrefForIntent,
    phoneNumberParses,
} from "./phone-primitives";

interface PhoneEntity {
    entityId: ActivityParentEntityId;
    entityType: AdvancedSearchEntityType;
}

type PhonePanelState = "closed" | "chooser" | "call" | "text";

const telecomTriggerTitle = (display: string, intent: PhoneActionIntent): string => {
    switch (intent) {
        case "chooser":
            return `Call or text ${display}`;
        case "call":
            return `Call ${display}`;
        case "text":
            return `Text ${display}`;
    }
    const exhaustiveCheck: never = intent;
    throw new Error(`Unhandled phone intent ${String(exhaustiveCheck)}`);
};

interface TelecomPhoneActionsProps {
    value: string;
    intent: PhoneActionIntent;
    entity: PhoneEntity;
    renderTrigger: (binding: PhoneTriggerBinding) => React.ReactNode;
}

const TelecomPhoneActions: React.FC<TelecomPhoneActionsProps> = props => {
    const { value, intent, entity, renderTrigger } = props;

    const [panelState, setPanelState] = React.useState<PhonePanelState>("closed");
    const refreshAgenda = useAgendaRefresh();
    const recordType = activityRecordTypeForEntityKind(entity.entityType);
    const callTypeId = useDefaultActivityTypeId("call", recordType);
    const smsTypeId = useDefaultActivityTypeId("sms", recordType);
    const parsed = parsePhone(value);

    const openIntent = React.useCallback(
        (e: React.MouseEvent<HTMLAnchorElement>) => {
            e.preventDefault();
            e.stopPropagation();
            setPanelState(intent);
        },
        [intent],
    );

    const openCall = React.useCallback(() => setPanelState("call"), []);
    const openText = React.useCallback(() => setPanelState("text"), []);
    const dismissChooser = React.useCallback(() => setPanelState("closed"), []);
    const dismissActivityPanel = React.useCallback(() => {
        setPanelState("closed");
        refreshAgenda();
    }, [refreshAgenda]);

    return (
        <>
            {renderTrigger({
                href: phoneHrefForIntent(parsed, intent),
                title: telecomTriggerTitle(parsed.display, intent),
                onClick: openIntent,
            })}
            {intent === "chooser" ? (
                <PhoneChooserPanel
                    isOpen={panelState === "chooser"}
                    onDismiss={dismissChooser}
                    display={parsed.display}
                    onCall={openCall}
                    onText={openText}
                />
            ) : null}
            <Panel
                isOpen={panelState === "call"}
                onDismiss={dismissActivityPanel}
                type={PanelType.medium}
                headerText="Phone Call"
                hasCloseButton={true}>
                <PhoneCall
                    phoneNumber={value}
                    entityId={entity.entityId}
                    activityTypeId={callTypeId}
                />
            </Panel>
            <SendSmsPanel
                isOpen={panelState === "text"}
                onDismiss={dismissActivityPanel}
                recipient={toPhoneNumberE164(value)}
                activityTypeId={smsTypeId}
                entityId={entity.entityId}
            />
        </>
    );
};

interface PhoneActionsProps {
    value: string;
    intent: PhoneActionIntent;
    entityId: ActivityParentEntityId | null;
    entityType: AdvancedSearchEntityType | null;
    renderTrigger: (binding: PhoneTriggerBinding) => React.ReactNode;
}

export const PhoneActions: React.FC<PhoneActionsProps> = props => {
    const { value, intent, entityId, entityType, renderTrigger } = props;

    const phoneLines = usePhoneLines();
    const entity: PhoneEntity | null =
        entityId && entityType ? { entityId, entityType } : null;

    if (entity && telecomAvailable(value, phoneLines.length)) {
        return (
            <TelecomPhoneActions
                value={value}
                intent={intent}
                entity={entity}
                renderTrigger={renderTrigger}
            />
        );
    }

    return (
        <DevicePhoneActions value={value} intent={intent} renderTrigger={renderTrigger} />
    );
};

interface PhoneLinkProps {
    value: string;
    entityId: ActivityParentEntityId | null;
    entityType: AdvancedSearchEntityType | null;
}

export const PhoneLink: React.FC<PhoneLinkProps> = props => {
    const { value, entityId, entityType } = props;

    if (!value) {
        return null;
    }

    if (!phoneNumberParses(value)) {
        return <span className={contactValueTextClasses}>{value}</span>;
    }

    const parsed = parsePhone(value);

    return (
        <PhoneActions
            value={value}
            intent="chooser"
            entityId={entityId}
            entityType={entityType}
            renderTrigger={binding => (
                <PhoneTriggerLink binding={binding} display={parsed.display} />
            )}
        />
    );
};

interface PhoneDisplayProps {
    value: string;
    label: string | null;
    entityId: ActivityParentEntityId | null;
    entityType: AdvancedSearchEntityType | null;
}

export const PhoneDisplay: React.FC<PhoneDisplayProps> = props => {
    const { value, label, entityId, entityType } = props;

    if (!value) {
        return null;
    }

    return (
        <ContactValueRow Icon={PhoneIcon}>
            <PhoneLink value={value} entityId={entityId} entityType={entityType} />
            <ContactValueLabel label={label} />
        </ContactValueRow>
    );
};
